import { Fragment } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';

import { useSoftwareEscrow } from 'src/context/software-escrow';
import { PartyNotificationSettings, StorageSettings, AssetVisibility } from 'src/containers/SoftwareEscrow/Technical';
import { useNotificationsSuggestChanges } from 'src/context/suggest-changes';
import { DisruptionNotificationEnum, DisruptionNotificationGracePeriodEnum } from 'src/graphql/schema';
import { SoftwareOnboardingMain } from 'src/components/SoftwareOnboarding';
import { Button } from '@/components/ui/button';

import { getDisruptionNotificationGracePeriod } from '../lib';

const NotificationSettings = ({ className }: { className?: string }) => {
  const {
    softwareEscrow: {
      beneficiaryActivityNotification,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
    },
  } = useSoftwareEscrow();
  const {
    state,
    updateBeneficiaryDepositNotification,
    updateBeneficiaryDisruptNotification,
    updateBeneficiaryDisruptNotificationGrace,
  } = useNotificationsSuggestChanges();

  const onDisruptionNotificationChange = (disruptionNotification: DisruptionNotificationEnum) => {
    const gracePeriodMap = {
      [DisruptionNotificationEnum.NoNotifications]: beneficiaryDisruptionNotificationGracePeriod,
      [DisruptionNotificationEnum.SendNotification]:
        beneficiaryDisruptionNotificationGracePeriod ?? DisruptionNotificationGracePeriodEnum.Immediate,
    };
    updateBeneficiaryDisruptNotification(disruptionNotification, gracePeriodMap[disruptionNotification]);
  };

  const disruptionNotificationGracePeriod = getDisruptionNotificationGracePeriod({
    disruptionNotification: state.beneficiaryDisruptionNotification ?? beneficiaryDisruptionNotification,
    newDisruptionNotificationGracePeriod: state.beneficiaryDisruptionNotificationGracePeriod,
    oldDisruptionNotificationGracePeriod: beneficiaryDisruptionNotificationGracePeriod,
  });

  return (
    <PartyNotificationSettings
      className={className}
      settings={{
        activityNotification: state.beneficiaryActivityNotification ?? beneficiaryActivityNotification,
        disruptionNotification: state.beneficiaryDisruptionNotification ?? beneficiaryDisruptionNotification,
        disruptionNotificationGracePeriod,
      }}
      onActivityNotificationChange={updateBeneficiaryDepositNotification}
      onDisruptionNotificationChange={onDisruptionNotificationChange}
      onGracePeriodChange={updateBeneficiaryDisruptNotificationGrace}
    />
  );
};

const StorageDepositorSettings = () => {
  const { state, updateStorageRegion, updateAssetVersionsLimit } = useNotificationsSuggestChanges();

  const onAssetVersionsLimitChange = (value: string) =>
    updateAssetVersionsLimit(value === 'full' ? null : Number(value));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
      <StorageSettings
        storageRegion={state.storageRegion ?? ''}
        assetVersionsLimit={state.assetVersionsLimit ?? undefined}
        onStorageRegionChange={updateStorageRegion}
        onAssetVersionsLimitChange={onAssetVersionsLimitChange}
      />
    </div>
  );
};

const AssetVisibilitySettings = () => {
  const {
    softwareEscrow: {
      beneficiarySharedInformation,
      beneficiaryActivityNotification,
      beneficiaryDisruptionNotification,
      beneficiaryDisruptionNotificationGracePeriod,
      currentCompanyRole,
    },
  } = useSoftwareEscrow();
  const { state, updateDepositDisplay } = useNotificationsSuggestChanges();

  const onAssetVisibilityChange = async (beneficiarySharedInformation: boolean | null) => {
    if (beneficiarySharedInformation === null) return;
    updateDepositDisplay({
      ...(!beneficiarySharedInformation
        ? {
            beneficiarySharedInformation: false,
            beneficiaryActivityNotification,
            beneficiaryDisruptionNotification,
            beneficiaryDisruptionNotificationGracePeriod,
          }
        : {
            beneficiarySharedInformation: true,
          }),
    });
  };

  return (
    <div className="w-[580px] max-w-full mx-auto space-y-10">
      <AssetVisibility
        onAssetVisibilityChange={onAssetVisibilityChange}
        assetVisibility={state.beneficiarySharedInformation ?? beneficiarySharedInformation}
        party={currentCompanyRole!}
      />
      {state.beneficiarySharedInformation && <NotificationSettings className="md:grid-cols-1" />}
    </div>
  );
};

const settingsStepsMap = {
  notification: {
    component: NotificationSettings,
    title: 'Notification settings for the beneficiary',
    description: 'Manage the information and the notifications you’ll receive.',
  },
  asset: {
    component: AssetVisibilitySettings,
    title: 'Notification settings for the beneficiary',
    description: 'Manage the information and notifications you’ll receive.',
  },
  storage: {
    component: StorageDepositorSettings,
    title: 'Storage settings',
    description: 'Configure the settings related to the deposited assets, and their storage in escrow.',
  },
} as const;

const Navigation = () => {
  const { steps, currentStep, setCurrentStep } = useNotificationsSuggestChanges();

  const onNextStep = () => setCurrentStep((prevStep) => prevStep + 1);

  const onPreviousStep = () => setCurrentStep((prevStep) => prevStep - 1);

  if (steps.length === 1) return null;

  return (
    <Fragment>
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-[50%] -translate-y-1/2 left-10 size-8 rounded-full border-border"
        onClick={onPreviousStep}
        disabled={currentStep === 0}
      >
        <ArrowLeft />
      </Button>
      <Button
        variant="secondary"
        size="icon"
        className="absolute top-[50%] -translate-y-1/2 right-10 size-8 rounded-full border-border"
        onClick={onNextStep}
        disabled={currentStep === steps.length - 1}
      >
        <ArrowRight />
      </Button>
    </Fragment>
  );
};

const BeneficiaryCreator = () => {
  const { steps, currentStep } = useNotificationsSuggestChanges();

  const {
    component: Component,
    title,
    description,
  } = settingsStepsMap[steps[currentStep ?? 0] as keyof typeof settingsStepsMap];

  return (
    <Fragment>
      <SoftwareOnboardingMain
        subtitle="Request changes to technical information:"
        title={title}
        description={description}
        classes={{
          subtitle: 'font-medium',
        }}
        className={steps.length > 1 ? 'px-10.5' : ''}
      >
        <Navigation />
        <div className="container mx-auto grow">
          <Component />
        </div>
      </SoftwareOnboardingMain>
    </Fragment>
  );
};

export default BeneficiaryCreator;
